import { useMemo } from "react"
import { useProgressData } from "./useProgressData"
import {
  IntelligenceHero,
  CategoryPillars,
  FocusInsightSection,
  NextStepsSection,
  OperationalStatusSection,
  MilestoneDetail,
  ProgressSkeleton,
  ProgressError,
  ProgressEmpty,
  computePII,
  buildCategoryStats,
  buildProgressVerdict,
} from "./components"

export function ProgressPage() {
  const { progress, overview, loading, error } = useProgressData()

  // Compute PII and category stats from progress data
  const pii = useMemo(
    () => progress ? computePII(progress) : null,
    [progress],
  )

  const categoryStats = useMemo(
    () => progress ? buildCategoryStats(progress) : [],
    [progress],
  )

  const verdict = useMemo(
    () => pii && progress
      ? buildProgressVerdict(pii, categoryStats, progress.seasonal)
      : null,
    [pii, progress, categoryStats],
  )

  if (loading) return <ProgressSkeleton />
  if (error) return <ProgressError error={error} />
  if (!progress || !progress.data_sufficient) return <ProgressEmpty />
  if (!pii || !verdict) return <ProgressEmpty />

  const totalPossible = progress.total_earned + progress.in_progress.length

  return (
    <div className="px-8 py-6 max-w-5xl">
      {/* Zone 1 — Intelligence Hero (dark editorial card) */}
      <IntelligenceHero
        pii={pii}
        verdict={verdict}
        totalEarned={progress.total_earned}
        totalPossible={totalPossible}
        tierBreakdown={progress.tier_breakdown}
      />

      {/* Zone 2 — Category Pillars (4 scored layers) */}
      <CategoryPillars
        categories={categoryStats}
        inProgress={progress.in_progress}
      />

      {/* Zone 3 — Focus Insight (deterministic nudges) */}
      <FocusInsightSection
        systemStatus={overview?.systemStatus}
        inProgress={progress.in_progress}
        seasonal={progress.seasonal}
      />

      {/* Zone 4 — Seasonal Readiness + Nearest Milestones */}
      <NextStepsSection
        seasonal={progress.seasonal}
        inProgress={progress.in_progress}
      />

      {/* Zone 5 — System Status (compact) */}
      <OperationalStatusSection
        systemStatus={overview?.systemStatus}
      />

      {/* Zone 6 — Milestone Detail (collapsed by default, below fold) */}
      <MilestoneDetail
        inProgress={progress.in_progress}
        earnedGroups={progress.earned_groups}
        tierBreakdown={progress.tier_breakdown}
      />
    </div>
  )
}
